import { Fragment, useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  REGISTER_ROLES,
  REGISTER_ROLE_LABEL,
  SYSTEM_ACCOUNT_WORD,
  inviteLinkMessage,
  inviteRegisterRolesIssue,
  type RegisterRole,
} from '@teslyn/domain';
import { CLIENT_ROLES, ROLE_LABEL, TENANT_ROLES, type ClientRole, type TenantRole } from '../access/roles.ts';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader, SectionHeader } from '../components/States.tsx';
import {
  addStaffRole,
  invite,
  loadMembers,
  removeClient,
  removeStaff,
  removeStaffRole,
  setClientRole,
  type ClientMember,
  type InviteRequest,
  type Members,
  type StaffMember,
} from './api.ts';

export interface UsersScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly currentUserId: string;
  /** Where invitation links land (this portal's address). */
  readonly redirectTo: string;
  /** Shown as a tab of a page (People, Company, Projects' Deleted list): no page title of its own. */
  readonly embedded?: boolean;
}

type Confirm =
  { readonly kind: 'staff'; readonly m: StaffMember } | { readonly kind: 'client'; readonly m: ClientMember };

const who = (m: { readonly email: string | null; readonly displayName: string }): string =>
  m.displayName || m.email || 'Unnamed user';

/**
 * Users & roles (tenant_admin, platform_admin): staff and client members, role changes, removal, invitations.
 * Staff may hold several roles (one membership row each; their rights are the union): ticking a role inserts a row,
 * unticking deletes it. Client users have one role per client organisation.
 */
export function UsersScreen({
  sb,
  tenantId,
  currentUserId,
  redirectTo,
  embedded = false,
}: UsersScreenProps): ReactElement {
  // inside a page with tabs (the menu consolidation): the tab's own header, under the page's title
  const Head = embedded ? SectionHeader : PageHeader;
  const [members, setMembers] = useState<Members | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ readonly ok: boolean; readonly text: string } | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [busyUser, setBusyUser] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoadError(null);
    loadMembers(sb, tenantId)
      .then(setMembers)
      .catch((e: unknown) => setLoadError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId]);

  useEffect(reload, [reload]);

  async function run(action: () => Promise<void>, done: string): Promise<void> {
    try {
      await action();
      setNotice({ ok: true, text: done });
    } catch (e: unknown) {
      setNotice({ ok: false, text: e instanceof Error ? e.message : String(e) });
    }
    reload();
  }

  return (
    <section className={embedded ? 'stack' : 'page'}>
      <Head
        title={embedded ? 'Sign-in and roles' : 'Users & roles'}
        subtitle="Everyone who can sign in to this portal. Access is by invitation only."
      >
        <Button variant="primary" onClick={() => setInviting(true)} disabled={!members || inviting}>
          Invite user
        </Button>
      </Head>

      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}

      {inviting && members && (
        <InviteForm
          orgs={members.orgs}
          onCancel={() => setInviting(false)}
          onSubmit={async (draft) => {
            const req: InviteRequest =
              draft.kind === 'tenant'
                ? {
                    tenant_id: tenantId,
                    email: draft.email,
                    kind: 'tenant',
                    roles: draft.roles,
                    // decision 867: the staff register roles the inviter chose
                    register_roles: draft.register_roles,
                  }
                : {
                    tenant_id: tenantId,
                    email: draft.email,
                    kind: 'client',
                    role: draft.role,
                    client_org_id: draft.client_org_id,
                  };
            const res = await invite(sb, req, redirectTo);
            setInviting(false);
            setNotice({
              ok: true,
              text:
                draft.kind === 'tenant' && res.link
                  ? // the staff register link on acceptance (decision 660)
                    inviteLinkMessage(draft.email, res.status, res.link)
                  : res.status === 'invited'
                    ? `Invitation sent to ${draft.email}.`
                    : `${draft.email} already had an account and now has the access you chose. They can sign in now.`,
            });
            reload();
          }}
        />
      )}

      {confirm && (
        <div className="panel confirm" role="dialog" aria-labelledby="confirm-title">
          <p id="confirm-title" className="body">
            Remove {who(confirm.m)}
            {confirm.kind === 'client' ? ` from ${confirm.m.clientOrgName}` : ''}? They lose access to this
            portal straight away.
          </p>
          <div className="row">
            <Button
              className="btn--danger"
              onClick={() => {
                const c = confirm;
                setConfirm(null);
                void run(
                  () =>
                    c.kind === 'staff'
                      ? removeStaff(sb, tenantId, currentUserId, c.m)
                      : removeClient(sb, tenantId, currentUserId, c.m),
                  `${who(c.m)} removed.`,
                );
              }}
            >
              Remove member
            </Button>
            <Button variant="quiet" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {loadError && <ErrorState message={loadError} onRetry={reload} />}
      {!members && !loadError && <Loading what="members" />}
      {members && (
        <>
          <h2 className="heading section-title">Staff</h2>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="label">Name</th>
                  <th className="label">Email</th>
                  <th className="label">Roles</th>
                  <th className="label">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {members.staff.length === 0 && (
                  <tr>
                    <td colSpan={4} className="muted">
                      No staff members yet.
                    </td>
                  </tr>
                )}
                {members.staff.map((m) => {
                  const self = m.userId === currentUserId;
                  const open = editing === m.userId;
                  return (
                    <Fragment key={m.userId}>
                      <tr>
                        <td>
                          {m.displayName || '–'}
                          {m.system && (
                            <span
                              className="chip caption"
                              title="A Braynewave system account: hidden from the organisation"
                            >
                              {SYSTEM_ACCOUNT_WORD}
                            </span>
                          )}
                        </td>
                        <td className="data">{m.email ?? 'Email not visible'}</td>
                        <td>
                          <ul className="chips" aria-label={`Roles of ${who(m)}`}>
                            {m.roles.map((r) => (
                              <li key={r} className="chip chip--role caption">
                                {ROLE_LABEL[r]}
                              </li>
                            ))}
                          </ul>
                        </td>
                        <td className="table__actions">
                          <Button
                            variant="quiet"
                            aria-label={`Change roles of ${who(m)}`}
                            aria-expanded={open}
                            onClick={() => setEditing(open ? null : m.userId)}
                          >
                            {open ? 'Done' : 'Change roles'}
                          </Button>
                          {!self && (
                            <Button
                              variant="quiet"
                              aria-label={`Remove ${who(m)}`}
                              onClick={() => setConfirm({ kind: 'staff', m })}
                            >
                              Remove
                            </Button>
                          )}
                        </td>
                      </tr>
                      {open && (
                        <tr className="table__detail">
                          <td colSpan={4}>
                            <RolePicker
                              legend={`Roles for ${who(m)}`}
                              selected={m.roles}
                              busy={busyUser === m.userId}
                              lockedRole={self ? 'tenant_admin' : null}
                              onToggle={(role, on) => {
                                setBusyUser(m.userId);
                                void run(
                                  () =>
                                    on
                                      ? addStaffRole(sb, tenantId, currentUserId, m, role)
                                      : removeStaffRole(sb, tenantId, currentUserId, m, role),
                                  on
                                    ? `${who(m)} is now also ${ROLE_LABEL[role]}.`
                                    : `${who(m)} is no longer ${ROLE_LABEL[role]}.`,
                                ).finally(() => setBusyUser(null));
                              }}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h2 className="heading section-title">Client users</h2>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th className="label">Organisation</th>
                  <th className="label">Name</th>
                  <th className="label">Email</th>
                  <th className="label">Role</th>
                  <th className="label">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {members.clients.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      No client users yet.
                    </td>
                  </tr>
                )}
                {members.clients.map((m) => (
                  <tr key={`${m.clientOrgId}:${m.userId}`}>
                    <td>{m.clientOrgName}</td>
                    <td>{m.displayName || '–'}</td>
                    <td className="data">{m.email ?? 'Email not visible'}</td>
                    <td>
                      <select
                        className="input input--inline"
                        aria-label={`Role for ${who(m)} at ${m.clientOrgName}`}
                        value={m.role}
                        onChange={(e) => {
                          const role = e.target.value as ClientRole;
                          void run(
                            () => setClientRole(sb, tenantId, currentUserId, m, role),
                            `${who(m)} is now ${ROLE_LABEL[role]}.`,
                          );
                        }}
                      >
                        {CLIENT_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="table__actions">
                      <Button
                        variant="quiet"
                        aria-label={`Remove ${who(m)} from ${m.clientOrgName}`}
                        onClick={() => setConfirm({ kind: 'client', m })}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Tick boxes for the five staff roles, applied one at a time. A member's only role cannot be unticked (a member needs
 * at least one role; "Remove" takes away all access), nor can `lockedRole` (an admin's own Tenant admin role).
 */
function RolePicker({
  legend,
  selected,
  busy,
  lockedRole,
  onToggle,
}: {
  readonly legend: string;
  readonly selected: readonly TenantRole[];
  readonly busy: boolean;
  readonly lockedRole: TenantRole | null;
  readonly onToggle: (role: TenantRole, on: boolean) => void;
}): ReactElement {
  return (
    <fieldset className="fieldset role-picker" aria-busy={busy}>
      <legend className="label muted">{legend}</legend>
      <div className="role-picker__options">
        {TENANT_ROLES.map((r) => {
          const on = selected.includes(r);
          const only = on && selected.length === 1;
          const locked = on && r === lockedRole;
          const why = locked
            ? 'You cannot remove your own Tenant admin role.'
            : only
              ? 'A member needs at least one role. Use Remove to take away all access.'
              : undefined;
          return (
            <label key={r} className="body row" title={why}>
              <input
                type="checkbox"
                checked={on}
                disabled={busy || only || locked}
                onChange={(e) => onToggle(r, e.target.checked)}
              />
              {ROLE_LABEL[r]}
            </label>
          );
        })}
      </div>
      <p className="caption muted">
        A person can hold several roles and can do everything each of them allows. Changes apply straight
        away.
      </p>
    </fieldset>
  );
}

type InviteDraft =
  | {
      readonly email: string;
      readonly kind: 'tenant';
      readonly roles: readonly TenantRole[];
      /** Decision 867: what the person is in the staff register, chosen here; never guessed. */
      readonly register_roles: readonly RegisterRole[];
    }
  | {
      readonly email: string;
      readonly kind: 'client';
      readonly role: ClientRole;
      readonly client_org_id: string;
    };

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function InviteForm({
  orgs,
  onSubmit,
  onCancel,
}: {
  readonly orgs: Members['orgs'];
  readonly onSubmit: (d: InviteDraft) => Promise<void>;
  readonly onCancel: () => void;
}): ReactElement {
  const [email, setEmail] = useState('');
  const [kind, setKind] = useState<'tenant' | 'client'>('tenant');
  const [roles, setRoles] = useState<readonly TenantRole[]>(['field_crew']);
  // decision 867: none ticked to start with; the inviter chooses
  const [registerRoles, setRegisterRoles] = useState<readonly RegisterRole[]>([]);
  const [clientRole, setClientRoleState] = useState<ClientRole>('client_viewer');
  const [org, setOrg] = useState(orgs[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const addr = email.trim().toLowerCase();
    if (!EMAIL.test(addr)) {
      setError('Enter a valid email address, for example jane@example.com.');
      return;
    }
    if (kind === 'tenant' && roles.length === 0) {
      setError('Choose at least one role.');
      return;
    }
    const registerIssue = kind === 'tenant' ? inviteRegisterRolesIssue(registerRoles) : null;
    if (registerIssue) {
      setError(registerIssue);
      return;
    }
    if (kind === 'client' && !org) {
      setError('Choose the client organisation.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSubmit(
        kind === 'tenant'
          ? {
              email: addr,
              kind,
              roles: TENANT_ROLES.filter((r) => roles.includes(r)),
              register_roles: REGISTER_ROLES.filter((r) => registerRoles.includes(r)),
            }
          : { email: addr, kind, role: clientRole, client_org_id: org },
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel stack" onSubmit={(e) => void submit(e)} aria-label="Invite user" noValidate>
      <h2 className="heading">Invite user</h2>
      <label className="label muted" htmlFor="invite-email">
        Email
      </label>
      <input
        id="invite-email"
        className="input"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <fieldset className="stack fieldset">
        <legend className="label muted">Access</legend>
        <label className="body row">
          <input type="radio" name="kind" checked={kind === 'tenant'} onChange={() => setKind('tenant')} />
          Staff of this company
        </label>
        <label className="body row">
          <input
            type="radio"
            name="kind"
            checked={kind === 'client'}
            onChange={() => setKind('client')}
            disabled={orgs.length === 0}
          />
          Client user{orgs.length === 0 ? ' (no client organisations yet)' : ''}
        </label>
      </fieldset>
      {kind === 'tenant' ? (
        <fieldset className="stack fieldset">
          <legend className="label muted">Roles</legend>
          {TENANT_ROLES.map((r) => (
            <label key={r} className="body row">
              <input
                type="checkbox"
                checked={roles.includes(r)}
                onChange={(e) => {
                  const on = e.target.checked;
                  setRoles((now) => (on ? [...now, r] : now.filter((x) => x !== r)));
                }}
              />
              {ROLE_LABEL[r]}
            </label>
          ))}
          <p className="caption muted">
            Tick every role the person needs; they can do everything each one allows.
          </p>
        </fieldset>
      ) : null}
      {kind === 'tenant' ? (
        <fieldset className="stack fieldset">
          <legend className="label muted">Staff register roles</legend>
          {REGISTER_ROLES.map((r) => (
            <label key={r} className="body row">
              <input
                type="checkbox"
                checked={registerRoles.includes(r)}
                onChange={(e) => {
                  const on = e.target.checked;
                  setRegisterRoles((now) => (on ? [...now, r] : now.filter((x) => x !== r)));
                }}
              />
              {REGISTER_ROLE_LABEL[r]}
            </label>
          ))}
          <p className="caption muted">
            What the person is in the staff register (a pilot, an operator, …). If they are not in the
            register yet, they are added with these roles when they accept, so they have them from their first
            sign-in; nothing is guessed from the roles above. Someone already in the register keeps the roles
            it has.
          </p>
        </fieldset>
      ) : (
        <>
          <label className="label muted" htmlFor="invite-org">
            Client organisation
          </label>
          <select id="invite-org" className="input" value={org} onChange={(e) => setOrg(e.target.value)}>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <label className="label muted" htmlFor="invite-client-role">
            Client role
          </label>
          <select
            id="invite-client-role"
            className="input"
            value={clientRole}
            onChange={(e) => setClientRoleState(e.target.value as ClientRole)}
          >
            {CLIENT_ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </>
      )}
      {error && (
        <p className="field__error caption" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <Button type="submit" variant="primary" busy={busy}>
          {busy ? 'Sending invitation…' : 'Send invitation'}
        </Button>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
